import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Container,
  SectionTitle,
  SectionSubtitle,
  FlexContainer,
} from "../../styles/Layout";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLinkedin,
  FaTwitter,
  FaGithub,
  FaPaperPlane,
  FaUser,
  FaComment,
} from "react-icons/fa";

const ContactSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
        circle at 30% 40%,
        rgba(0, 212, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 70% 60%,
        rgba(255, 0, 128, 0.1) 0%,
        transparent 50%
      );
    z-index: 1;
  }
`;

const ContactContent = styled(FlexContainer)`
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled(motion.div)`
  flex: 1;
  padding-right: var(--spacing-xl);

  @media (max-width: 768px) {
    padding-right: 0;
    margin-bottom: var(--spacing-xl);
  }
`;

const InfoTitle = styled.h3`
  font-size: 2.2rem;
  margin-bottom: var(--spacing-lg);
  background: linear-gradient(
    135deg,
    var(--text-primary) 0%,
    var(--neon-blue) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    border-radius: 2px;
  }
`;

const InfoText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: var(--spacing-xl);
  color: var(--text-secondary);
`;

const ContactDetails = styled.div`
  margin-top: var(--spacing-xl);
`;

const ContactItem = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.04) 100%
    );
    transform: translateX(10px);
    border-color: var(--neon-blue);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-lg);
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 8px 20px rgba(0, 212, 255, 0.3);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 100%
    );
    border-radius: 16px;
  }
`;

const ContactText = styled.div`
  h4 {
    margin-bottom: var(--spacing-sm);
    color: var(--text-primary);
    font-size: 1.2rem;
    font-weight: 600;
  }

  p {
    color: var(--text-secondary);
    margin-bottom: 0;
    line-height: 1.6;
  }
`;

const ContactForm = styled(motion.form)`
  flex: 1;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: var(--spacing-xl);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
`;

const FormTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: var(--spacing-xl);
  text-align: center;
  background: linear-gradient(
    135deg,
    var(--text-primary) 0%,
    var(--neon-green) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
  position: relative;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
`;

const InputWrapper = styled.div`
  position: relative;

  .input-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    font-size: 1.1rem;
    z-index: 2;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  color: var(--text-primary);
  transition: all 0.3s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--neon-blue);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.04) 100%
    );

    + .input-icon {
      color: var(--neon-blue);
    }
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  color: var(--text-primary);
  resize: vertical;
  min-height: 150px;
  transition: all 0.3s ease;
  font-family: "Inter", sans-serif;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--neon-blue);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.3);
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.08) 0%,
      rgba(255, 255, 255, 0.04) 100%
    );
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  color: white;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 212, 255, 0.3);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 212, 255, 0.5);
    transform: translateY(-3px);

    &::before {
      left: 100%;
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
  justify-content: center;
`;

const SocialLink = styled(motion.a)`
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: linear-gradient(
      135deg,
      var(--neon-blue) 0%,
      var(--neon-magenta) 100%
    );
    color: white;
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 212, 255, 0.4);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Reset form
    setFormData({ name: "", email: "", subject: "", message: "" });
    setIsSubmitting(false);

    // Show success message (you can implement a toast notification here)
    alert("Message sent successfully! We'll get back to you soon.");
  };

  return (
    <ContactSection id="contact">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>
            Get In Touch With{" "}
            <img
              src={require("../../assets/logos/Labs_colour .png")}
              alt="GeoX Labs Logo"
              style={{
                height: "50px",
                verticalAlign: "middle",
                marginLeft: "10px",
              }}
            />
          </SectionTitle>
          <SectionSubtitle>
            Have a project in mind? Let's discuss how we can help turn your
            vision into reality with our premium design and cutting-edge
            technology solutions.
          </SectionSubtitle>
        </motion.div>

        <ContactContent>
          <ContactInfo
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <InfoTitle>Let's Connect</InfoTitle>
            <InfoText>
              We'd love to hear about your project and explore how our expertise
              in premium design and advanced technology can help bring your
              ideas to life. Contact us through the form or using the
              information below to start the conversation.
            </InfoText>

            <ContactDetails>
              <ContactItem
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <IconWrapper>
                  <FaPhone />
                </IconWrapper>
                <ContactText>
                  <h4>Phone</h4>
                  <p>+91 99026-91168</p>
                </ContactText>
              </ContactItem>

              <ContactItem
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <IconWrapper>
                  <FaEnvelope />
                </IconWrapper>
                <ContactText>
                  <h4>Email</h4>
                  <p>support@geosentryx.com</p>
                </ContactText>
              </ContactItem>

              <ContactItem
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <IconWrapper>
                  <FaMapMarkerAlt />
                </IconWrapper>
                <ContactText>
                  <h4>Address</h4>
                  <p>
                    #15, 3rd Cross Jabbar Block,
                    <br />
                    PG Halli Vyalikaval Extn,
                    <br />
                    Bangalore North, Karnataka - 560003 India
                  </p>
                </ContactText>
              </ContactItem>
            </ContactDetails>

            <SocialLinks>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaGithub />
              </SocialLink>
            </SocialLinks>
          </ContactInfo>

          <ContactForm
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
          >
            <FormTitle>Send us a message</FormTitle>

            <FormGroup>
              <FormLabel>Name</FormLabel>
              <InputWrapper>
                <FormInput
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <FaUser className="input-icon" />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>Email</FormLabel>
              <InputWrapper>
                <FormInput
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <FaEnvelope className="input-icon" />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>Subject</FormLabel>
              <InputWrapper>
                <FormInput
                  type="text"
                  name="subject"
                  placeholder="How can we help?"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
                <FaPaperPlane className="input-icon" />
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <FormLabel>Message</FormLabel>
              <InputWrapper>
                <FormTextarea
                  name="message"
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
                <FaComment className="input-icon" />
              </InputWrapper>
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </Container>
    </ContactSection>
  );
};

export default Contact;
